import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { fetcher } from "@/lib/api";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import Loader from "@/components/Loader";

const Meta = ({ label, value }) => (
  <div>
    <p className="text-xs tracking-[0.18em] uppercase text-ash mb-1.5">{label}</p>
    <p className="text-cream">{value}</p>
  </div>
);

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const { data: cs, isLoading, isError } = useQuery({
    queryKey: ["case-study", slug],
    queryFn: () => fetcher(`/case-studies/${slug}`),
  });

  if (isLoading) return <Loader label="Loading case study" />;
  if (isError || !cs) {
    return (
      <div className="pt-44 pb-32 text-center px-6" data-testid="case-study-error">
        <p className="text-ash">Case study not found.</p>
        <Link to="/work" className="mt-4 inline-block text-cream underline">Back to work</Link>
      </div>
    );
  }

  return (
    <article data-testid="case-study-detail" className="pt-32 lg:pt-40 pb-24 lg:pb-32">
      <Seo
        title={cs.title}
        description={cs.subtitle}
        path={`/work/${cs.slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CreativeWork",
          name: cs.title,
          headline: cs.title,
          description: cs.subtitle,
          author: { "@type": "Person", name: "Nishant Gathwal" },
          datePublished: cs.year,
          keywords: (cs.tags || []).join(", "),
        }}
      />
      <div className="mx-auto max-w-5xl px-6 lg:px-10">
        <Link
          to="/work"
          data-testid="back-to-work"
          className="group inline-flex items-center gap-2 text-ash hover:text-cream transition-colors mb-12"
        >
          <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" /> All work
        </Link>

        <Reveal>
          <span className="rounded-full border border-white/15 px-3 py-1 text-xs text-cream/80">{cs.category}</span>
          <h1 className="mt-6 font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight text-cream text-balance">
            {cs.title}
          </h1>
          <p className="mt-5 max-w-2xl text-ash text-lg lg:text-xl leading-relaxed">{cs.subtitle}</p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 py-8 border-y border-white/10">
            <Meta label="Role" value={cs.role} />
            <Meta label="Year" value={cs.year} />
            <Meta label="Duration" value={cs.duration} />
            <Meta label="Platform" value={cs.platform} />
          </div>
        </Reveal>
      </div>

      <div className="mx-auto max-w-3xl px-6 lg:px-10 mt-16 space-y-16">
        <Reveal>
          <section data-testid="cs-overview">
            <h2 className="font-heading text-sm tracking-[0.18em] uppercase text-ash mb-4">Overview</h2>
            <p className="text-cream/90 text-lg leading-relaxed">{cs.overview}</p>
          </section>
        </Reveal>

        <Reveal>
          <section data-testid="cs-challenge">
            <h2 className="font-heading text-sm tracking-[0.18em] uppercase text-ash mb-4">The challenge</h2>
            <p className="text-cream/90 text-lg leading-relaxed">{cs.challenge}</p>
          </section>
        </Reveal>

        <Reveal>
          <section data-testid="cs-process">
            <h2 className="font-heading text-sm tracking-[0.18em] uppercase text-ash mb-8">Process</h2>
            <div className="space-y-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
              {(cs.process || []).map((p, i) => (
                <div key={i} className="bg-ink p-6 lg:p-8 flex gap-6">
                  <span className="font-heading text-2xl font-light text-ash tabular-nums shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-heading text-lg text-cream mb-2">{p.title}</h3>
                    <p className="text-ash leading-relaxed">{p.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </Reveal>
      </div>

      <div className="mx-auto max-w-3xl px-6 lg:px-10 mt-16 space-y-16">
        <Reveal>
          <section data-testid="cs-outcome">
            <h2 className="font-heading text-sm tracking-[0.18em] uppercase text-ash mb-4">Outcome</h2>
            <p className="text-cream/90 text-lg leading-relaxed">{cs.outcome}</p>
          </section>
        </Reveal>

        {cs.metrics?.length > 0 && (
          <Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 rounded-2xl overflow-hidden border border-white/10">
              {cs.metrics.map((m, i) => (
                <div key={i} data-testid={`cs-metric-${i}`} className="bg-ink p-8 text-center">
                  <p className="font-heading font-light text-4xl lg:text-5xl tracking-tight text-cream">{m.value}</p>
                  <p className="mt-2 text-ash text-sm">{m.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        )}
      </div>

      <div className="mx-auto max-w-5xl px-6 lg:px-10 mt-24">
        <div className="rounded-2xl border border-white/10 bg-surface p-10 lg:p-14 text-center">
          <h3 className="font-heading font-light text-2xl sm:text-3xl tracking-tight text-cream">
            Have a project like this?
          </h3>
          <Link
            to="/contact"
            data-testid="cs-contact-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-cream text-ink font-medium px-7 py-3.5 hover:bg-white transition-colors"
          >
            Let's talk <ArrowUpRight size={18} />
          </Link>
        </div>
      </div>
    </article>
  );
}
